import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import type {
  RhcDashboardData,
  RhcDashboardFacility,
  RhcFilingStatus,
  RhcProviderBasedFacility,
} from "@services/networkDashboard";
import {
  abbreviateFacilityType,
  facilityLocation,
  formatCcnLabel,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";

export const RHC_TYPE_CODE = "RHC";

export const RHC_DASHBOARD_SUBTITLE =
  "Cost report (CMS-222) · 4200 productivity standard · all-inclusive rate · rural designation";

export const RHC_ROSTER_SUBTITLE =
  "RHC-specific parameters · click a facility to view its dashboard";

export type RhcTone = "ok" | "warn" | "neutral";

export type RhcKpiTile = {
  id: string;
  label: string;
  value: string;
  sublabel: string;
  valueTone?: "primary" | "neutral";
};

export type RhcRosterRow = {
  key: string;
  facilityId: number | null;
  name: string;
  metaLine: string;
  costReportLabel: string;
  costReportTone: RhcTone;
  productivityLabel: string;
  productivityTone: RhcTone;
  airStatusLabel: string;
  airStatusTone: RhcTone;
  ruralLabel: string;
  ruralTone: RhcTone;
  ruralValid: boolean;
  nextFilingLabel: string;
  nextFilingTone: RhcTone;
  missingSourcesLabel: string | null;
  providerBased: boolean;
  dashboardFacility: MyFacilityListRow | null;
};

export type RhcProviderBasedRow = {
  key: string;
  facilityId: number | null;
  name: string;
  metaLine: string;
  parentFacilityName: string;
  parentBedsLabel: string;
  airCapLabel: string;
  airCapTone: RhcTone;
  costPerVisitLabel: string;
  providerBasedSinceLabel: string;
  dashboardFacility: MyFacilityListRow | null;
};

export type RhcDashboardModel = {
  facilityCount: number;
  kpis: RhcKpiTile[];
  roster: RhcRosterRow[];
  providerBased: RhcProviderBasedRow[];
};

function toDashboardFacility(
  facility: FacilityHierarchyInput,
  facilityId: number,
): MyFacilityListRow {
  return {
    facility_id: facilityId,
    id: facilityId,
    facility_name: facility.facility_name || `Facility ${facilityId}`,
    city: typeof facility.city === "string" ? facility.city : "",
    state: typeof facility.state === "string" ? facility.state : "",
    full_address: facilityLocation(facility),
    facility_type:
      (typeof facility.facility_type === "string" && facility.facility_type) || "RHC",
    facility_type_slug:
      (typeof facility.facility_type_slug === "string" && facility.facility_type_slug) ||
      "rural_health_clinic",
    org_id: 0,
    org_name: "",
  };
}

export function collectLinkedRhCFacilities(
  members: FacilityHierarchyInput[],
  selectedId: number | null,
): FacilityHierarchyInput[] {
  return members.filter((member) => {
    const id = getFacilityHierarchyId(member);
    if (selectedId != null && id === selectedId) return false;
    return abbreviateFacilityType(member) === RHC_TYPE_CODE;
  });
}

function buildMetaLineFromParts(
  ccn: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  linked?: FacilityHierarchyInput,
): string {
  if (linked) {
    return buildMetaLine(linked);
  }
  return [
    ccn ? formatCcnLabel(ccn) : "CCN —",
    [city, state].filter(Boolean).join(", ") || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function buildMetaLine(facility: FacilityHierarchyInput): string {
  const ids = networkRegistryDisplayIds(facility);
  const ccn = ids.ccn ? formatCcnLabel(ids.ccn) : "";
  const location = facilityLocation(facility);
  return [ccn, location !== "—" ? location : ""].filter(Boolean).join(" · ");
}

function normalizeCcn(value: string | null | undefined): string {
  return String(value || "")
    .replace(/\D/g, "")
    .padStart(6, "0");
}

function linkedFacilityByCcn(
  linkedRhCs: FacilityHierarchyInput[],
): Map<string, FacilityHierarchyInput> {
  const result = new Map<string, FacilityHierarchyInput>();
  linkedRhCs.forEach((facility) => {
    const ccn = networkRegistryDisplayIds(facility).ccn;
    if (ccn) result.set(normalizeCcn(ccn), facility);
  });
  return result;
}

function formatShortDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function countLabel(value: number | null | undefined): string {
  return value == null ? "—" : String(value);
}

function filingStatusLabel(status: RhcFilingStatus | null | undefined): {
  label: string;
  tone: RhcTone;
} {
  switch (status) {
    case "FILED":
      return { label: "Filed", tone: "ok" };
    case "DUE_SOON":
      return { label: "Due soon", tone: "warn" };
    case "PENDING":
      return { label: "Pending", tone: "warn" };
    case "OVERDUE":
      return { label: "Overdue", tone: "warn" };
    default:
      return { label: "—", tone: "neutral" };
  }
}

function nextFilingPresentation(
  status: RhcFilingStatus | null | undefined,
  nextFilingDate: string | null | undefined,
): { label: string; tone: RhcTone } {
  if (status === "OVERDUE") {
    return { label: "Past due", tone: "warn" };
  }
  if (nextFilingDate) {
    return { label: formatShortDate(nextFilingDate), tone: "neutral" };
  }
  return { label: "—", tone: "neutral" };
}

function productivityPresentation(row: RhcDashboardFacility): {
  label: string;
  tone: RhcTone;
} {
  const visits = row.visits_per_clinical_fte;
  if (visits == null) return { label: "—", tone: "neutral" };
  if (row.productivity_standard_applicable === false) {
    return { label: `${Math.round(visits)} visits/FTE`, tone: "neutral" };
  }
  const capacity = row.productivity_standard_capacity ?? 4200;
  const label = `${Math.round(visits)}/${capacity}`;
  return {
    label,
    tone: row.productivity_standard_met ? "ok" : "warn",
  };
}

function airStatusPresentation(
  status: RhcDashboardFacility["air_status"],
): { label: string; tone: RhcTone } {
  if (status === "RECONCILED") return { label: "Reconciled", tone: "ok" };
  if (status === "PENDING_SETTLEMENT") {
    return { label: "Pending settlement", tone: "warn" };
  }
  return { label: "—", tone: "neutral" };
}

function ruralDesignationPresentation(
  status: RhcDashboardFacility["rural_designation_status"],
): { label: string; tone: RhcTone; valid: boolean } {
  if (status === "ACTIVE") return { label: "Valid", tone: "ok", valid: true };
  if (status === "GRANDFATHERED") {
    return { label: "Grandfathered", tone: "warn", valid: false };
  }
  if (status === "NONE") return { label: "No", tone: "neutral", valid: false };
  return { label: "—", tone: "neutral", valid: false };
}

function missingSourcesLabel(
  sources: string[] | undefined,
  ccn: string | null | undefined,
): string | null {
  const list = sources?.length ? sources : ccn ? [] : ["ccn"];
  if (!list.length) return null;
  const pretty = list.map((source) =>
    source.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
  );
  return `Missing: ${pretty.join(", ")}`;
}

function rowName(
  row: RhcDashboardFacility,
  linked: FacilityHierarchyInput | undefined,
  index: number,
): string {
  return row.name || linked?.facility_name || `RHC ${index + 1}`;
}

function mapProviderBasedRow(
  row: RhcProviderBasedFacility,
  linkedByCcn: Map<string, FacilityHierarchyInput>,
  index: number,
): RhcProviderBasedRow {
  const linked = linkedByCcn.get(normalizeCcn(row.ccn));
  const facilityId =
    (linked ? getFacilityHierarchyId(linked) : null) ?? row.facility_id ?? null;
  const key =
    facilityId != null ? `pb-${facilityId}` : `pb-idx-${index}-${row.ccn ?? "unknown"}`;
  const airCapExposed = row.air_cap_exposed === true;
  return {
    key,
    facilityId,
    name: row.name || linked?.facility_name || `RHC ${index + 1}`,
    metaLine: buildMetaLineFromParts(row.ccn, row.city, row.state, linked),
    parentFacilityName: row.parent_facility_name || "—",
    parentBedsLabel: row.parent_bed_count == null ? "—" : String(row.parent_bed_count),
    airCapLabel: airCapExposed ? "Yes · cap-exposed" : row.air_cap_exposed === false ? "No" : "—",
    airCapTone: airCapExposed ? "warn" : "ok",
    costPerVisitLabel: formatCurrency(row.cost_per_visit),
    providerBasedSinceLabel: formatShortDate(row.provider_based_effective_date),
    dashboardFacility:
      linked && facilityId != null ? toDashboardFacility(linked, facilityId) : null,
  };
}

function linkedIdentityRows(
  linkedRhCs: FacilityHierarchyInput[],
): RhcDashboardFacility[] {
  return linkedRhCs.map((facility) => {
    const ids = networkRegistryDisplayIds(facility);
    return {
      facility_id: getFacilityHierarchyId(facility),
      ccn: ids.ccn ?? null,
      name: facility.facility_name ?? null,
      city: typeof facility.city === "string" ? facility.city : null,
      state: typeof facility.state === "string" ? facility.state : null,
      provider_based: null,
      filing_status: null,
      next_filing_date: null,
      visits_per_clinical_fte: null,
      productivity_standard_applicable: null,
      productivity_standard_met: null,
      productivity_standard_capacity: null,
      air_status: null,
      rural_designation_status: null,
      missing_sources: ids.ccn ? [] : ["ccn"],
    };
  });
}

export function buildLiveRhCDashboardModel(
  data: RhcDashboardData,
  linkedRhCs: FacilityHierarchyInput[],
): RhcDashboardModel {
  const sourceFacilities =
    (data.facilities ?? []).length > 0
      ? data.facilities
      : linkedIdentityRows(linkedRhCs);
  const linkedByCcn = linkedFacilityByCcn(linkedRhCs);
  const roster: RhcRosterRow[] = sourceFacilities.map((row, index) => {
    const linked = linkedByCcn.get(normalizeCcn(row.ccn));
    const facilityId =
      (linked ? getFacilityHierarchyId(linked) : null) ?? row.facility_id ?? null;
    const filing = filingStatusLabel(row.filing_status);
    const productivity = productivityPresentation(row);
    const air = airStatusPresentation(row.air_status);
    const rural = ruralDesignationPresentation(row.rural_designation_status);
    const nextFiling = nextFilingPresentation(row.filing_status, row.next_filing_date);
    return {
      key: facilityId != null ? `rhc-${facilityId}` : `rhc-idx-${index}`,
      facilityId,
      name: rowName(row, linked, index),
      metaLine: buildMetaLineFromParts(row.ccn, row.city, row.state, linked),
      costReportLabel: filing.label,
      costReportTone: filing.tone,
      productivityLabel: productivity.label,
      productivityTone: productivity.tone,
      airStatusLabel: air.label,
      airStatusTone: air.tone,
      ruralLabel: rural.label,
      ruralTone: rural.tone,
      ruralValid: rural.valid,
      nextFilingLabel: nextFiling.label,
      nextFilingTone: nextFiling.tone,
      missingSourcesLabel: missingSourcesLabel(row.missing_sources, row.ccn),
      providerBased: row.provider_based === true,
      dashboardFacility:
        linked && facilityId != null ? toDashboardFacility(linked, facilityId) : null,
    };
  });

  const providerBased = (data.provider_based_facilities ?? []).map((row, index) =>
    mapProviderBasedRow(row, linkedByCcn, index),
  );

  const summary = data.summary ?? {};
  const reportedCount = data.total_count ?? summary.facility_count;
  const facilityCount = Math.max(reportedCount ?? 0, roster.length);
  const pendingCost =
    (summary.pending_count ?? 0) +
    (summary.due_soon_count ?? 0) +
    (summary.overdue_count ?? 0);
  const productivityMet = summary.productivity_standard_met_count;
  const productivityApplicable = summary.productivity_standard_applicable_count;
  const reconciled = summary.reconciled_air_count;
  const pendingAir = summary.pending_air_count;
  const activeRural = summary.active_rural_designation_count;

  const kpis: RhcKpiTile[] =
    facilityCount === 0
      ? [
          {
            id: "cost",
            label: "Cost report status",
            value: "—",
            sublabel: "No RHCs linked yet",
          },
          {
            id: "productivity",
            label: "Average productivity",
            value: "—",
            sublabel: "CMS standard: 4,200",
          },
          {
            id: "air",
            label: "All-inclusive rate",
            value: "—",
            sublabel: "Awaiting linked RHCs",
          },
          {
            id: "rural",
            label: "Rural designation",
            value: "—",
            sublabel: "HPSA data unavailable",
            valueTone: "primary",
          },
        ]
      : [
          {
            id: "cost",
            label: "Cost report status",
            value: `${countLabel(summary.filed_count)} filed`,
            sublabel: `${countLabel(pendingCost)} pending · ${countLabel(summary.overdue_count)} overdue`,
          },
          {
            id: "productivity",
            label: "Average productivity",
            value:
              summary.average_visits_per_clinical_fte == null
                ? "—"
                : `${Math.round(summary.average_visits_per_clinical_fte)} visits/FTE`,
            sublabel:
              productivityApplicable == null
                ? "CMS standard: 4,200"
                : `CMS standard: 4,200 · met by ${countLabel(productivityMet)} of ${countLabel(productivityApplicable)}`,
          },
          {
            id: "air",
            label: "All-inclusive rate",
            value: `${countLabel(reconciled)} reconciled`,
            sublabel: `${countLabel(pendingAir)} pending settlement`,
          },
          {
            id: "rural",
            label: "Rural designation",
            value: `${countLabel(activeRural)} valid`,
            sublabel: `${countLabel(summary.grandfathered_rural_designation_count)} grandfathered · ${countLabel(summary.unknown_rural_designation_count)} unknown`,
            valueTone: "primary",
          },
        ];

  return { facilityCount, kpis, roster, providerBased };
}

/** Empty model for linked members before API load (AB#11010). */
export function buildRhCDashboardModel(
  linkedRhCs: FacilityHierarchyInput[],
): RhcDashboardModel {
  return buildLiveRhCDashboardModel(
    {
      summary: {
        facility_count: linkedRhCs.length,
        filed_count: null,
        pending_count: null,
        overdue_count: null,
        due_soon_count: null,
        average_visits_per_clinical_fte: null,
        visits_per_clinical_fte_coverage_count: null,
        productivity_standard_applicable_count: null,
        productivity_standard_met_count: null,
        reconciled_air_count: null,
        pending_air_count: null,
        active_rural_designation_count: null,
        grandfathered_rural_designation_count: null,
        unknown_rural_designation_count: null,
      },
      facilities: linkedRhCs.map((facility) => ({
        ccn: networkRegistryDisplayIds(facility).ccn ?? null,
        name: facility.facility_name ?? null,
        city: typeof facility.city === "string" ? facility.city : null,
        state: typeof facility.state === "string" ? facility.state : null,
        provider_based: null,
        filing_status: null,
        next_filing_date: null,
        visits_per_clinical_fte: null,
        productivity_standard_applicable: null,
        productivity_standard_met: null,
        productivity_standard_capacity: null,
        air_status: null,
        rural_designation_status: null,
        missing_sources: [],
      })),
      provider_based_facilities: [],
      total_count: linkedRhCs.length,
    },
    linkedRhCs,
  );
}

export function rhcToneColor(tone: RhcTone): string {
  if (tone === "ok") return "#0b6a0b";
  if (tone === "warn") return "#a4262c";
  return "#323130";
}
